import { useState, useEffect, useRef } from 'react';
import { useProfile } from 'src/models';
import { useApiQueryParams } from 'src/lib/services/api-query-params';
import { SortParam, FilterParam } from 'src/lib/services/api-query-params/api-query-params.types';
import { useSaveRequestPreferencesMutation } from './use-save-request-preferences-mutation';

// Define types for request preferences
export interface RequestPreferences {
  sorts?: SortParam[];
  filters?: FilterParam[];
}

// Define the interface for the profile preferences
export interface ProfilePreferences {
  requestList?: RequestPreferences;
}

export const useRequestPreferences = () => {
  const { filters: activeQuery, setFilters } = useApiQueryParams();
  const profile = useProfile({ enabled: false });

  const saveRequestPreferencesMutation = useSaveRequestPreferencesMutation();

  const profilePreferences = profile.preferences?.requestList as RequestPreferences | undefined;

  // Local state for managing preferences in the sheet - initialized as an empty object
  const [localPreferences, setLocalPreferences] = useState<RequestPreferences>({});

  // Flag to check if URL parameters exist
  const hasUrlParams = (): boolean => {
    // Check URL for query parameters
    const urlSearchParams = new URLSearchParams(window.location.search);
    return urlSearchParams.toString().length > 0;
  };

  const applyPreferences = () => {
    // Create a clean filters object for application
    const filtersToApply = {
      filters: localPreferences.filters || profilePreferences?.filters || undefined,
      sorts: localPreferences.sorts || profilePreferences?.sorts || undefined,
    };

    // Apply saved preferences
    setFilters(filtersToApply);
  };

  // Helper: normalize sorts/filters for order-insensitive deep comparison
  const isOnlyPreferences = (): boolean => {
    const normalizeSorts = (sorts?: SortParam[]) => {
      if (!sorts?.length) return [] as string[];
      const map = new Map<string, { id: string; desc: boolean }>();
      for (const s of sorts) {
        if (!s?.id) continue;
        map.set(s.id, { id: s.id, desc: !!s.desc });
      }
      return Array.from(map.values())
        .map((s) => `${s.id}|${s.desc ? 'desc' : 'asc'}`)
        .sort();
    };

    const normalizeValue = (val: any): string => {
      if (Array.isArray(val)) {
        // Sort array values for order-insensitive compare
        return JSON.stringify([...val].sort());
      }
      if (val && typeof val === 'object') {
        // Stable stringify object by sorting keys
        const sortObj = (o: any): any => {
          if (Array.isArray(o)) return o.map(sortObj);
          if (o && typeof o === 'object') {
            return Object.keys(o)
              .sort()
              .reduce((acc: any, k: string) => {
                acc[k] = sortObj(o[k]);
                return acc;
              }, {} as any);
          }
          return o;
        };
        return JSON.stringify(sortObj(val));
      }
      return val === undefined || val === null ? '' : String(val);
    };

    const normalizeFilters = (filters?: FilterParam[]) => {
      if (!filters?.length) return [] as string[];
      const set = new Set<string>();
      for (const f of filters) {
        if (!f?.id) continue;
        const op = f.operator ?? '';
        const v = normalizeValue(f.value);
        set.add(`${f.id}|${op}|${v}`);
      }
      return Array.from(set.values()).sort();
    };

    const eqArr = (a: string[], b: string[]) => {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
      return true;
    };

    const activeSorts = normalizeSorts(activeQuery?.sorts);
    const activeFilters = normalizeFilters(activeQuery?.filters);
    const prefSorts = normalizeSorts(profilePreferences?.sorts);
    const prefFilters = normalizeFilters(profilePreferences?.filters);

    return eqArr(activeSorts, prefSorts) && eqArr(activeFilters, prefFilters);
  };

  const updateLocalSorts = (sorts: SortParam[]) => {
    setLocalPreferences((prev) => ({
      ...prev,
      sorts: sorts.length > 0 ? sorts : undefined,
    }));
  };

  const updateLocalFilters = (filters: FilterParam[]) => {
    setLocalPreferences((prev) => ({
      ...prev,
      filters: filters.length > 0 ? filters : undefined,
    }));
  };

  const savePreferences = async () => {
    // Create a clean preferences object that only includes non-empty arrays
    const requestPreferences: RequestPreferences = {};

    // Only include sorts if there are any
    if (localPreferences.sorts && localPreferences.sorts.length > 0) {
      requestPreferences.sorts = localPreferences.sorts;
    }

    // Only include filters if there are any
    if (localPreferences.filters && localPreferences.filters.length > 0) {
      requestPreferences.filters = localPreferences.filters;
    }

    // Only save preferences if there's something to save
    const preferencesToSave: ProfilePreferences = {};

    if (Object.keys(requestPreferences).length > 0) {
      preferencesToSave.requestList = requestPreferences;
    }

    await saveRequestPreferencesMutation.mutateAsync([preferencesToSave], {
      onSuccess: async () => {
        await profile.query.refetch();
        applyPreferences();
      },
    });
  };

  const clearPreferences = async (
    nextQuery?: { sorts?: SortParam[]; filters?: FilterParam[] },
  ) => {
    // Clear local preferences
    setLocalPreferences({});

    saveRequestPreferencesMutation.mutate([{}], {
      onSuccess: async () => {
        await profile.query.refetch();
        setFilters(nextQuery ?? { sorts: undefined, filters: undefined });
      },
    });
  };

  return {
    preferences: localPreferences,
    profilePreferences,
    setLocalPreferences,
    updateLocalSorts,
    updateLocalFilters,
    savePreferences,
    clearPreferences,
    applyPreferences,
    isLoading: saveRequestPreferencesMutation.isLoading,
    hasUrlParams,
    hasPreferences: !!(
      (localPreferences.sorts?.length || 0) > 0 || (localPreferences.filters?.length || 0) > 0
    ),
    isOnlyPreferences,
  };
};
